import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { CheckIcon } from "@/components/ui/check-animated";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { XIcon } from "@/components/ui/x-animated";
import { cn } from "@/lib/utils";
import type { ActiveTradingViewComposeChecklist } from "../../utils/activeTradingViewComposeChecklist";
import type {
  ComposeChecklistItem,
  ComposeChecklistItemState,
} from "../../utils/activeTradingViewComposeChecklist";
import { STRATEGY_PINE_BADGE_CLASS } from "../../views/Dashboard/Strategies/strategyTablePineBadges";

const CHECKLIST_FONT = "font-[family-name:var(--font-primary)]";
const CHECKLIST_ICON_SIZE = 16;

const CHECKLIST_FIELD_LABEL_CLASS = cn(
  CHECKLIST_FONT,
  "flex w-full flex-wrap items-center gap-x-2 gap-y-1 text-sm font-normal text-gray-500 dark:text-white"
);

const CHECKLIST_FIELD_DESC_CLASS = cn(
  CHECKLIST_FONT,
  "text-sm font-normal leading-5 text-gray-500 dark:text-white"
);

function stateIconClass(state: ComposeChecklistItemState): string {
  if (state === "done") {
    return "shrink-0 text-[color:var(--tessera-pnl-positive)]";
  }
  if (state === "warning") {
    return "shrink-0 text-amber-600 dark:text-amber-400";
  }
  if (state === "blocked") {
    return "shrink-0 text-red-600 dark:text-red-400";
  }
  return "shrink-0 text-gray-400";
}

function ChecklistStateIcon({ state }: { state: ComposeChecklistItemState }): React.ReactElement {
  const className = cn("mt-0.5", stateIconClass(state));

  if (state === "done") {
    return (
      <CheckIcon
        size={CHECKLIST_ICON_SIZE}
        className={className}
        aria-hidden
        data-strategy-compose-checklist-icon="check"
      />
    );
  }

  return (
    <XIcon
      size={CHECKLIST_ICON_SIZE}
      className={className}
      aria-hidden
      data-strategy-compose-checklist-icon="x"
      data-strategy-compose-checklist-icon-state={state}
    />
  );
}

function ChecklistRow({ item }: { item: ComposeChecklistItem }): React.ReactElement {
  const titleId = `strategy-compose-checklist-${item.id}-title`;

  return (
    <Field
      orientation="horizontal"
      data-strategy-compose-checklist-item={item.id}
      data-strategy-compose-checklist-state={item.state}
      className="items-start gap-3 rounded-md border border-border/80 bg-muted/20 px-3 py-2.5"
      aria-label={`${item.title}: ${item.state === "done" ? "complete" : "incomplete"}`}
    >
      <ChecklistStateIcon state={item.state} />
      <FieldContent className="min-w-0 gap-1">
        <FieldLabel className={cn(CHECKLIST_FIELD_LABEL_CLASS, "opacity-100 peer-disabled:opacity-100")}>
          <span id={titleId} data-strategy-compose-checklist-title="">
            {item.title}
          </span>
          {item.badgeLabel && item.badgeVariant ? (
            <Badge
              variant={item.badgeVariant}
              data-strategies-pine-badge={item.pineBadgeKind ?? item.id}
              className={STRATEGY_PINE_BADGE_CLASS}
            >
              {item.badgeLabel}
            </Badge>
          ) : null}
        </FieldLabel>
        <FieldDescription className={cn(CHECKLIST_FIELD_DESC_CLASS, "opacity-100")}>
          {item.description}
        </FieldDescription>
      </FieldContent>
    </Field>
  );
}

export type ActiveTradingViewPineComposeChecklistProps = {
  checklist: ActiveTradingViewComposeChecklist;
};

export function ActiveTradingViewPineComposeChecklist({
  checklist,
}: ActiveTradingViewPineComposeChecklistProps): React.ReactElement {
  const { phase, heading, subheading, items } = checklist;

  return (
    <section data-strategy-compose-checklist="" data-strategy-compose-checklist-phase={phase} className="space-y-3">
      <div className="space-y-1">
        <h3
          className={cn(
            CHECKLIST_FONT,
            "text-sm font-normal text-[color:var(--brand-tropical-surf)]"
          )}
        >
          {heading}
        </h3>
        {subheading ? (
          <p className={cn(CHECKLIST_FONT, "text-sm font-normal text-gray-500 dark:text-white")}>{subheading}</p>
        ) : null}
      </div>

      <FieldGroup className="gap-2">{items.map((item) => <ChecklistRow key={item.id} item={item} />)}</FieldGroup>
    </section>
  );
}
